import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { cn, initials } from '@/lib/utils';
import { ActionMenu, ActionMenuItem, ActionMenuSeparator } from './ui/action-menu';

export function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('shell');
  if (!user) return null;

  const roleLine = user.isPlatformUser ? user.platformRole : user.roles.map((role) => role.name).join(', ');
  const ending = Boolean(user.impersonation);

  return (
    <ActionMenu
      label={t('menu.account')}
      className="w-[16rem]"
      trigger={
        <button
          type="button"
          aria-label={t('menu.account')}
          className={cn(
            'flex h-9 items-center gap-2 rounded-full ps-1 pe-2 text-sm transition-colors focus-ring',
            'hover:bg-muted data-[state=open]:bg-muted',
          )}
        >
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
            {initials(user.displayName)}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      }
    >
      <div className="flex items-center gap-2.5 px-2.5 pb-2 pt-1.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
          {initials(user.displayName)}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">{user.displayName}</span>
          <span className="block truncate text-xs text-muted-foreground">{roleLine || user.email}</span>
        </span>
      </div>
      <ActionMenuSeparator />
      {!user.isPlatformUser && !user.impersonation && (
        <ActionMenuItem onSelect={() => navigate('/account/security')} icon={<ShieldCheck />}>
          {t('menu.security')}
        </ActionMenuItem>
      )}
      <ActionMenuItem
        destructive
        icon={<LogOut />}
        onSelect={async () => {
          await signOut();
          navigate(ending ? '/impersonate' : '/login', { replace: true });
        }}
      >
        {ending ? t('menu.end_support_session') : t('menu.sign_out')}
      </ActionMenuItem>
    </ActionMenu>
  );
}
